/**
 * On-demand event summarization using the AI fallback chain.
 * Summaries are cached in MemoryCache with 1-hour TTL.
 */

import type { NormalizedEvent } from '@/sources/base-source';
import type { SummaryResult } from './types';
import { executeWithFallback } from './fallback-chain';
import { SUMMARIZE_SYSTEM_PROMPT, buildSummarizePrompt } from './prompts';
import { cache } from '@/storage/cache';
import { eventBus } from '@/core/events';

const SUMMARY_TTL_MS = 60 * 60 * 1000; // 1 hour
const CACHE_PREFIX = 'ai:summary:';

/**
 * Summarize an event using the AI fallback chain.
 * Returns cached result on second call within TTL.
 */
export async function summarizeEvent(event: NormalizedEvent): Promise<SummaryResult> {
  const cacheKey = `${CACHE_PREFIX}${event.id}`;

  // Check cache first
  const cached = cache.get<SummaryResult>(cacheKey);
  if (cached) {
    return { ...cached, cached: true };
  }

  const response = await executeWithFallback({
    prompt: buildSummarizePrompt(event.title, event.description, event.source),
    systemPrompt: SUMMARIZE_SYSTEM_PROMPT,
    maxTokens: 200,
    temperature: 0.3,
  });

  const result: SummaryResult = {
    summary: response.text,
    provider: response.provider,
    cached: false,
  };

  // Cache the result
  cache.set(cacheKey, result, SUMMARY_TTL_MS);

  // Notify listeners
  eventBus.emit('ai:summary:complete', {
    eventId: event.id,
    summary: response.text,
    provider: response.provider,
  });

  return result;
}
